/**
 * Platform dashboard — AI, Software Engineering, Unit Hierarchy.
 *
 * The layout is `Dashboard v5.dc.html`, unchanged: a module switcher over one
 * flat card per module, a six-up strip of hairline-separated metric cells, and
 * section strips that break the card into runs of panels. The design's type
 * scale and spacing are kept exactly — 10px uppercase mono labels, 23px mono
 * figures, 11px captions, 12.5px row text, 11px/14px cell padding.
 *
 * What is the application's rather than the design's: the accent is indigo, not
 * the design's teal; the ink ramp, hairlines and canvas are the app's; and the
 * typeface is `--app-font`, so the Appearance setting reaches this page.
 * "Good" is blue rather than green, so the palette stays on the indigo→cyan
 * half. Semantic colour is otherwise unchanged — amber warns, red is critical.
 *
 * Figures come from the live API through `platformDashboardService`. Where an
 * endpoint fails or a metric has no source the cell shows an em dash and says
 * which — a zero and an unavailable number are different claims.
 *
 * Panels the design specifies but the API cannot feed (AI agents, RAG models,
 * per-ministry rollups, unit growth by month, record completeness) are dropped
 * rather than shown as permanent em dashes. Where a figure exists but not in the
 * form the design asked for, the panel states what it actually shows.
 *
 * It lives here rather than in `pages/dashboard.tsx` because that filename is
 * taken by the legacy counts screen still served at `/analytics-dashboard`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CSSProperties, ReactNode } from 'react';
import DashboardSkeleton from '../../components/common/dashboard-skeleton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useHasPermission } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import {
  platformDashboardService,
  ago, compact, countOf, dur, money, num, pct, rate, secs, until,
  type PlatformDashboardData, type RangeId,
} from '../../services/platform-dashboard.service';
import './platform-dashboard.css';
import { backgroundJobService } from '../../services/background-job.service';

// ---------------------------------------------------------------------------

type ViewId = 'ai' | 'se' | 'uh';

const VIEWS: { id: ViewId; code: string; label: string; subtitle: string }[] = [
  { id: 'ai', code: 'AI', label: 'AI', subtitle: 'AI operations' },
  { id: 'se', code: 'SE', label: 'Software Engineering', subtitle: 'System health' },
  { id: 'uh', code: 'UH', label: 'Unit Hierarchy', subtitle: 'Organisation structure' },
];

const RANGES: { id: RangeId; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: '7 days' },
  { id: 'month', label: '30 days' },
];
const PERIOD: Record<RangeId, string> = { today: 'today', week: 'last 7 days', month: 'last 30 days' };

/* Every colour here is a CSS custom property defined in the stylesheet, read
   through `var()` so the palette lives in exactly one place. */
const V = {
  accent: 'var(--pd-accent)',
  ok: 'var(--pd-ok)', okFill: 'var(--pd-ok-fill)',
  warn: 'var(--pd-warn)', warnFill: 'var(--pd-warn-fill)',
  bad: 'var(--pd-bad)', badFill: 'var(--pd-bad-fill)',
  mid: 'var(--pd-mid-fill)',
  ink4: 'var(--pd-ink-4)',
};

/** The rotation for breakdown lists — the app's indigo→cyan family. */
const WHEEL = ['#6366F1', '#8B5CF6', '#06B6D4', '#3B82F6', '#A855F7', '#0EA5E9', '#EC4899', '#F43F5E'];
const wheel = (i: number) => WHEEL[i % WHEEL.length];

/* Lower-cases first so SCREAMING_CASE enum values ("SCHEDULE", "FAILED") come
   out as words rather than shouting across a table of sentence-case rows. */
const titleCase = (s: string) =>
  s.toLowerCase().replace(/[_-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

/* Title-casing mangles the acronyms the API returns — "API" becomes "Api". */
const LABEL: Record<string, string> = {
  inapp: 'In-app', email: 'Email', sms: 'SMS', push: 'Push', whatsapp: 'WhatsApp',
  api: 'API', ui: 'UI', thirdparty: 'Third party', db: 'Database',
  config: 'Config', screen: 'Screen', service: 'Service', function: 'Function',
};
const label = (k: string) => LABEL[k.toLowerCase()] ?? titleCase(k);

/**
 * Spreads hourly rows across all 24 slots, filling the hours the API omitted
 * with zero.
 *
 * These endpoints only return hours that saw traffic. Plotting the returned
 * rows directly gives a chart with however many points exist but an axis
 * labelled 00:00 → 23:00, so a quiet night silently shifts every point along
 * the axis and the peak is reported at the wrong hour.
 */
const overHours = <T,>(rows: T[] | undefined | null, hourOf: (r: T) => number, valueOf: (r: T) => number) => {
  if (!rows?.length) return null;
  const slots = new Array<number>(24).fill(0);
  const byHour = new Map<number, T>();
  for (const r of rows) {
    const h = hourOf(r);
    if (h >= 0 && h < 24) { slots[h] = valueOf(r); byHour.set(h, r); }
  }
  return { slots, byHour };
};

const HOUR_LABELS = ['00:00', '06:00', '12:00', '18:00', '23:00'];

/** Evenly spaced axis labels for a dated series, so every chart states its span. */
const axisLabels = (stamps: (string | null | undefined)[], withTime: boolean, count = 4): string[] => {
  const fmt = (iso: string | null | undefined) => {
    if (!iso) return '';
    /* The audit trend labels its daily buckets date-only ("2026-08-26"), and
       `Date.parse` reads a bare date as UTC midnight. Rendered in a timezone
       behind UTC that lands on the previous day, so every daily bucket would be
       labelled a day early. A date-only string is built as LOCAL midnight; an
       ISO datetime ("2026-09-01T15:00:00") already carries its own offset. */
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.exec(iso);
    const d = dateOnly
      ? new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)))
      : new Date(Date.parse(iso));
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', withTime
      ? { hour: '2-digit', minute: '2-digit' }
      : { day: '2-digit', month: 'short' });
  };
  if (stamps.length <= count) return stamps.map(fmt).filter(Boolean);
  const step = (stamps.length - 1) / (count - 1);
  return Array.from({ length: count }, (_, i) => fmt(stamps[Math.round(i * step)])).filter(Boolean);
};

/** Reads one severity bucket regardless of how the source cased its key. */
const sevAt = (rec: Record<string, number> | undefined, ...names: string[]): number => {
  if (!rec) return 0;
  const want = new Set(names.map((n) => n.toLowerCase()));
  let total = 0;
  for (const [k, v] of Object.entries(rec)) {
    if (want.has(k.trim().toLowerCase()) && typeof v === 'number') total += v;
  }
  return total;
};

/**
 * Eval pass rates arrive as a FRACTION, not a percentage.
 *
 * `summary.passRate` is `passed / scored` and the backend documents it 0.0-1.0;
 * `headlineKpis.aiQuality.passRate` is the same scale. Every other rate on this
 * page is already 0-100, so these were being rendered as "0.8%" instead of
 * "80.0%" — and, compared against a gate expressed in percent, every completed
 * run read "Below gate".
 */
const passPct = (fraction: number | null | undefined): number | null =>
  fraction === null || fraction === undefined || !Number.isFinite(fraction)
    ? null
    : fraction * 100;

/**
 * The gate a run was actually judged against.
 *
 * Each run snapshots its suite's `grader.passThreshold` (0.0-1.0) at start, so
 * suites with different bars are judged by their own. 0.7 is the default the
 * backend documents, used only for legacy runs that recorded no grader — it is
 * not a number this page invents.
 */
/**
 * Where a rate stops reading as healthy and starts reading as degraded.
 *
 * These colour a figure; they never change one. No endpoint publishes "what
 * counts as good", so unlike the eval gate — which each run carries — these are
 * a presentation convention, declared once here rather than repeated as magic
 * numbers at five call sites. Delivery is held to a higher bar than model
 * success because an undelivered notification is a lost message.
 */
const OK_RATE = 95;
const OK_DELIVERY_RATE = 99;

const EVAL_GATE_DEFAULT = 0.7;
const gateOf = (run: { grader?: { passThreshold?: number | null } | null }): number => {
  const t = run.grader?.passThreshold;
  return (typeof t === 'number' && Number.isFinite(t) ? t : EVAL_GATE_DEFAULT) * 100;
};

/** Did this run miss its gate? The run's own PASS/FAIL verdict wins when it
 *  recorded one; otherwise its pass rate is compared to its own threshold. */
const belowGate = (run: {
  status?: string;
  grader?: { passThreshold?: number | null } | null;
  summary?: { passRate?: number | null; passFail?: string | null };
}): boolean => {
  if (run.status !== 'completed') return false;
  const verdict = run.summary?.passFail;
  if (verdict) return verdict.trim().toUpperCase() === 'FAIL';
  const pr = passPct(run.summary?.passRate);
  return pr !== null && pr < gateOf(run);
};

/** Sorts a `{ key: count }` map into biggest-first rows a bar list can draw. */
const ranked = (rec: Record<string, number> | undefined | null) =>
  Object.entries(rec ?? {})
    .filter(([, v]) => typeof v === 'number' && v > 0)
    .sort((a, b) => b[1] - a[1]);

const sevTone = (s: string | undefined) => {
  const k = (s ?? '').toLowerCase();
  if (k.includes('crit') || k === 'fatal') return { c: V.bad, f: 'var(--pd-bad)' };
  if (k.includes('high') || k === 'error') return { c: V.bad, f: V.badFill };
  if (k.includes('med') || k.includes('warn')) return { c: V.warn, f: V.warnFill };
  return { c: V.ok, f: V.okFill };
};

const chip = (colour: string): CSSProperties => ({
  color: colour, background: `color-mix(in srgb, ${colour} 12%, transparent)`,
  border: `1px solid color-mix(in srgb, ${colour} 26%, transparent)`,
});

// ---------------------------------------------------------------------------
// Primitives — the design's own blocks
// ---------------------------------------------------------------------------

const Cell = ({ k, lead, end, children, onClick }: {
  k: string; lead?: boolean; end?: boolean; children: ReactNode; onClick?: () => void;
}) => (
  <div className={`pd-cell${lead ? ' lead' : ''}${end ? ' end' : ''}${onClick ? ' link' : ''}`}
    onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}>
    <span className="k">{k}</span>
    {children}
  </div>
);

const Strip = ({ title, note }: { title: string; note?: ReactNode }) => (
  <div className="pd-strip">
    <i /><span className="t">{title}</span><span className="grow" />
    {note && <span className="n">{note}</span>}
  </div>
);

const Panel = ({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) => (
  <div className="pd-panel">
    <div className="pd-phead"><span className="k">{title}</span><span className="grow" />
      {right && <span className="r">{right}</span>}</div>
    {children}
  </div>
);

const Delta = ({ value, good }: { value: string | null; good: 'up' | 'down' }) => {
  if (!value) return null;
  const flat = /^[+−-]?0(\.\d)?%$/.test(value);
  const rising = value.startsWith('+');
  const cls = flat ? 'flat' : (good === 'up' ? rising : !rising) ? 'good' : 'bad';
  return <span className={`pd-d ${cls}`}>{value}</span>;
};

const Empty = ({ what }: { what: string }) => (
  <div className="pd-empty">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="14" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M12 8v4.5M12 16h.01" />
    </svg>{what}
  </div>
);

const Split = ({ a, b, colour = V.accent }: { a: number; b: number; colour?: string }) => (
  <div className="pd-split">
    <span style={{ flex: Math.max(a, 0.001), background: colour }} />
    <span style={{ flex: Math.max(b, 0.001) }} />
  </div>
);

const Donut = ({ pct: p, colour = V.accent, size = 38 }: { pct: number | null; colour?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 38 38" aria-hidden="true" style={{ flex: 'none' }}>
    <circle cx="19" cy="19" r="15" fill="none" stroke="var(--pd-accent-track)" strokeWidth="5" />
    {p !== null && (
      <circle cx="19" cy="19" r="15" fill="none" stroke={colour} strokeWidth="5" pathLength={100}
        strokeDasharray={`${Math.max(0, Math.min(100, p))} 100`} strokeLinecap="butt"
        transform="rotate(-90 19 19)" />
    )}
  </svg>
);

/** The design's multi-arc donut, for a breakdown rather than a single share. */
const Ring = ({ parts, size = 62 }: { parts: { v: number; c: string }[]; size?: number }) => {
  const total = parts.reduce((n, p) => n + p.v, 0) || 1;
  let off = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 38 38" style={{ flex: 'none' }} aria-hidden="true">
      {parts.map((p, i) => {
        const share = (p.v / total) * 100;
        const arc = (
          <circle key={i} cx="19" cy="19" r="15" fill="none" stroke={p.c} strokeWidth="6"
            pathLength={100} strokeDasharray={`${share.toFixed(1)} 100`}
            strokeDashoffset={(-off).toFixed(1)} transform="rotate(-90 19 19)" />
        );
        off += share;
        return arc;
      })}
    </svg>
  );
};

const Spark = ({ series, colour = V.accent }: { series: number[]; colour?: string }) => {
  if (series.length < 2) return null;
  const max = Math.max(...series), min = Math.min(...series), span = max - min || 1;
  const pts = series.map((v, i) => `${(i / (series.length - 1)) * 56},${15 - ((v - min) / span) * 12}`).join(' ');
  return (
    <svg width="56" height="18" viewBox="0 0 56 18" aria-hidden="true" style={{ flex: 'none' }}>
      <polyline points={pts} fill="none" stroke={colour} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

/** The design's compact area chart — 62px tall, no axis furniture. */
const Line = ({ series, id, labels, onHover, onLeave }: {
  series: number[]; id: string; labels?: string[];
  onHover?: (i: number, x: number, y: number) => void; onLeave?: () => void;
}) => {
  const max = Math.max(...series), min = Math.min(...series);
  /* Baseline at zero for count series. Scaling between min and max makes a
     2,700→3,200 week look like a tenfold swing, because the floor of the plot
     is 2,700 rather than nothing — the shape has to be proportional to the
     value to be read honestly. A series that dips below zero keeps its own
     minimum, since there is no zero floor to anchor to. */
  const lo = min >= 0 ? 0 : min;
  const span = (max - lo) || 1;
  const n = series.length;
  const x = (i: number) => (i / Math.max(n - 1, 1)) * 320;
  const y = (v: number) => 54 - ((v - lo) / span) * 46;
  const pts = series.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <>
      <svg viewBox="0 0 320 62" preserveAspectRatio="none" className="pd-line" role="img"
        aria-label={`Ranges from ${min} to ${max} across ${n} points`}
        onMouseMove={onHover ? (e) => {
          const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const i = Math.max(0, Math.min(n - 1, Math.round(((e.clientX - r.left) / r.width) * (n - 1))));
          onHover(i, e.clientX, e.clientY);
        } : undefined}
        onMouseLeave={onLeave}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#6366F1" stopOpacity=".28" />
            <stop offset="1" stopColor="#6366F1" stopOpacity=".02" />
          </linearGradient>
        </defs>
        <line x1="0" y1="54" x2="320" y2="54" stroke="rgba(15,23,42,.10)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        <polyline points={`0,62 ${pts} 320,62`} fill={`url(#${id})`} stroke="none" />
        <polyline points={pts} fill="none" stroke="#6366F1" strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
      </svg>
      {labels && <div className="pd-scale">{labels.map((l) => <span key={l}>{l}</span>)}</div>}
    </>
  );
};

const Bar = ({ name, sub, value, width, colour, nameW = 118, valW = 52 }: {
  name: ReactNode; sub?: ReactNode; value: ReactNode; width: number;
  colour: string; nameW?: number; valW?: number;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
    <div className="pd-bar">
      <span className="nm" style={{ width: nameW }}>{name}</span>
      <span className="track"><i style={{ width: `${Math.max(2, Math.min(100, width))}%`, background: colour }} /></span>
      <span className="v" style={{ width: valW }}>{value}</span>
    </div>
    {sub && <span className="pd-barsub" style={{ paddingLeft: nameW + 9 }}>{sub}</span>}
  </div>
);

// ---------------------------------------------------------------------------

const PlatformDashboard = () => {
  const navigate = useAppNavigate();
  // The explorer is a platform-wide read; so is its button.
  const canExplore = useHasPermission(PERMISSIONS.DATA_EXPLORER_READ);
  const [view, setView] = useState<ViewId>('ai');
  const [range, setRange] = useState<RangeId>('week');
  const [data, setData] = useState<PlatformDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tip, setTip] = useState<{ x: number; y: number; node: ReactNode } | null>(null);

  /**
   * Only the newest request may write state.
   *
   * Switching range starts a fetch and does not cancel the one in flight, so
   * two loads could be racing. Whichever settled last used to win regardless of
   * which range was actually selected — click Today then quickly 30 days and
   * the slower Today response could land afterwards, leaving thirty-day-old
   * figures under a highlighted "Today" tab. Each load takes a ticket; a load
   * that finds its ticket superseded discards its result and leaves `loading`
   * alone, because the newer load owns it.
   */
  const reqId = useRef(0);
  const load = useCallback(async (r: RangeId) => {
    const id = reqId.current + 1;
    reqId.current = id;
    setLoading(true);
    const next = await platformDashboardService.load(r);
    if (reqId.current !== id) return;
    setData(next);
    setLoading(false);
  }, []);
  useEffect(() => { void load(range); }, [range, load]);

  const c = data?.counts ?? null;
  const ov = data?.overview ?? null;
  const perf = data?.performance ?? null;
  const pa = data?.prompts ?? null;
  const uu = data?.unitUsage ?? null;
  const ua = data?.userActivity ?? null;
  const rv = data?.review ?? null;
  const el = data?.errors ?? null;
  const au = data?.audit ?? null;
  const nt = data?.notifications ?? null;
  const jb = data?.jobs ?? null;
  const runs = data?.jobRuns ?? null;
  // A run stores only its jobId, so the name comes from the job list — the same
  // lookup (and cache key) the Background Jobs → Runs tab uses.
  const jobsQ = useQuery({
    queryKey: ['bg-jobs', 'all-for-runs'],
    queryFn: () => backgroundJobService.listJobs({ page: 1, page_size: 200 }),
    staleTime: 60_000,
  });
  const jobNameById = useMemo(
    () => new Map((jobsQ.data?.items ?? []).map((j) => [j._id, j.jobName])),
    [jobsQ.data],
  );
  const evr = data?.evalRuns ?? null;
  const fb = data?.feedback ?? null;

  const period = PERIOD[range];

  /* ---------------------------------------------------------------- AI ---- */

  const hourly = useMemo(
    () => overHours(perf?.byHour, (h) => h.hour, (h) => h.calls),
    [perf],
  );
  const aiSeries = hourly?.slots ?? null;
  /* Read off the normalised slots, so the reported peak hour is the array index
     and always matches the point the chart draws. */
  const peakHour = useMemo(() => {
    if (!aiSeries) return null;
    let best = 0;
    for (let h = 1; h < 24; h += 1) if (aiSeries[h] > aiSeries[best]) best = h;
    return aiSeries[best] > 0 ? { hour: best, calls: aiSeries[best] } : null;
  }, [aiSeries]);

  /* "Models in use" counts models that actually ran in the window, not a status
     flag — `byModel` only contains a model if it served a call. */
  const modelsUsed = perf?.byModel?.length ?? null;
  const modelMax = perf?.byModel?.length ? Math.max(...perf.byModel.map((m) => m.calls)) : 0;
  const latMax = perf?.responseTimeDistribution?.length
    ? Math.max(...perf.responseTimeDistribution.map((b) => b.count)) : 0;

  const evalBelow = evr ? evr.filter(belowGate).length : null;

  const execPerUser = ov && (ov.currentPeriod?.activeUsers ?? 0) > 0
    ? ov.currentPeriod.totalPromptExecutions / ov.currentPeriod.activeUsers
    : null;

  /* ---------------------------------------------------------------- SE ---- */

  /**
   * Fold `bySeverity` into buckets. The backend groups on the stored
   * `errorSeverity` verbatim, so the key casing is whatever is in the
   * collection — "CRITICAL", "Critical" and "critical" are all possible. Keys
   * are lower-cased before matching, and anything unrecognised lands in `other`
   * rather than vanishing, so the ring always sums to the reported count.
   */
  const sev = useMemo(() => {
    if (!el?.bySeverity) return null;
    const b = { critical: 0, high: 0, medium: 0, low: 0, other: 0 };
    for (const [key, stat] of Object.entries(el.bySeverity)) {
      const k = key.trim().toLowerCase();
      const n = stat?.count ?? 0;
      if (!n) continue;
      if (k.startsWith('crit') || k === 'fatal' || k === 'blocker') b.critical += n;
      else if (k.startsWith('high') || k === 'error' || k === 'major') b.high += n;
      else if (k.startsWith('med') || k.startsWith('warn') || k === 'moderate') b.medium += n;
      else if (k.startsWith('low') || k === 'info' || k === 'minor' || k === 'trivial') b.low += n;
      else b.other += n;
    }
    let total = b.critical + b.high + b.medium + b.low + b.other;
    /* `bySeverity` and `summary.totalErrors` are separate aggregations, so they
       can disagree — an error with no severity recorded appears in one and not
       the other. Any shortfall joins `other`, which keeps the ring, its
       percentages and the headline count telling the same story. */
    const headline = el.summary?.totalErrors;
    if (typeof headline === 'number' && headline > total) {
      b.other += headline - total;
      total = headline;
    }
    return total > 0 ? { ...b, total } : null;
  }, [el]);

  const sevRows = sev ? [
    { name: 'Critical', v: sev.critical, c: 'var(--pd-bad)' },
    { name: 'High', v: sev.high, c: V.badFill },
    { name: 'Medium', v: sev.medium, c: V.warnFill },
    { name: 'Low', v: sev.low, c: V.okFill },
    { name: 'Unclassified', v: sev.other, c: V.ink4 },
  ].filter((r) => r.v > 0) : [];

  const errTimeline = el?.timeline?.length ? el.timeline : null;
  const errTotal = el?.summary?.totalErrors ?? null;

  /* Half-window means, not first-vs-last: on a noisy series the last bucket is
     not the trend, and comparing endpoints reports the wrong direction. */
  const errDelta = useMemo(() => {
    const s = errTimeline?.map((b) => b.total) ?? rv?.trends?.errorsPerDay;
    if (!s || s.length < 4) return null;
    /* Equal halves. With an odd number of buckets the middle one was landing in
       the second half, biasing the comparison by one bucket's worth of volume. */
    const h = Math.floor(s.length / 2);
    const mean = (a: number[]) => a.reduce((x, y) => x + y, 0) / a.length;
    const first = mean(s.slice(0, h));
    const second = mean(s.slice(s.length - h));
    return first === 0 ? null : ((second - first) / first) * 100;
  }, [errTimeline, rv]);

  const errByType = useMemo(() => {
    const rows = Object.entries(el?.byType ?? {})
      .map(([k, v]) => ({ name: label(k), count: v.count, unique: v.unique, users: v.affectedUsers }))
      .filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
    return rows.length ? rows : null;
  }, [el]);

  const errByHour = useMemo(
    () => overHours(el?.trends, (t) => t.hour,
      (t) => (t.API ?? 0) + (t.UI ?? 0) + (t.SCREEN ?? 0) + (t.THIRDPARTY ?? 0) + (t.FUNCTION ?? 0))?.slots ?? null,
    [el],
  );

  const logLevels = useMemo(() => {
    if (!au?.byLevel) return null;
    const rows = [
      { name: 'Info', v: au.byLevel.info ?? 0, c: V.accent },
      { name: 'Warning', v: au.byLevel.warning ?? 0, c: V.warnFill },
      { name: 'Error', v: au.byLevel.error ?? 0, c: V.badFill },
    ];
    const total = rows.reduce((n, r) => n + r.v, 0);
    return total > 0 ? { rows, total } : null;
  }, [au]);

  const byLayer = useMemo(() => ranked(au?.byLayer), [au]);
  const layerMax = byLayer.length ? byLayer[0][1] : 0;
  const endpointMax = au?.topEndpoints?.length ? au.topEndpoints[0].count : 0;
  const templateMax = nt?.topTemplates?.length ? nt.topTemplates[0].count : 0;

  /* Each dated chart carries its own axis and hover stamps, built from the same
     rows as its values, so a label can never describe a different point than
     the one plotted. `withTime` follows the selected range: hours within a day,
     dates across a week or month. */
  const withTime = range === 'today';

  const auditSeries = useMemo(() => {
    const t = au?.trend;
    if (!t) return null;
    const s = range === 'today' ? t.last24Hours : range === 'week' ? t.last7Days : t.last30Days;
    if (!s?.length) return null;
    const stamps = s.map((p) => p.timestamp);
    return {
      values: s.map((p) => p.count),
      labels: axisLabels(stamps, withTime),
      stamps: axisLabels(stamps, withTime, s.length),
    };
  }, [au, range, withTime]);

  const notifSeries = useMemo(() => {
    const t = nt?.deliveryTimeline;
    if (!t?.length) return null;
    const stamps = t.map((p) => p.timestamp);
    return {
      values: t.map((p) => p.total),
      delivered: t.map((p) => p.delivered),
      failed: t.map((p) => p.failed),
      labels: axisLabels(stamps, withTime),
      stamps: axisLabels(stamps, withTime, t.length),
    };
  }, [nt, withTime]);

  /**
   * The error-level share of logged activity.
   *
   * Numerator and denominator both come from the audit log's own level
   * breakdown over the same windowed query, so an error is by construction one
   * of the events counted underneath it and the share cannot exceed 100%.
   *
   * Two earlier denominators were wrong for the same underlying reason — they
   * divided by a quantity from a different collection, which has no subset
   * relationship with the numerator. AI prompt executions came first; audit
   * events against the error-log system's `totalErrors` came second, and that
   * one produced a visible 101% because error_logs and the audit log are
   * separate records of separate things. This is the only true rate available.
   */
  const errRate = logLevels && logLevels.total > 0
    ? Math.min((logLevels.rows.find((r) => r.name === 'Error')?.v ?? 0) / logLevels.total * 100, 100)
    : null;

  /**
   * Review Centre signals.
   *
   * This endpoint was being fetched and then almost entirely ignored — only its
   * error trend was read, as a fallback. Its two genuinely useful figures now
   * feed the module headers, which is the one place each belongs: an eval pass
   * rate for the window is an AI quality signal, and rolled-up system health is
   * exactly what the SE header is for. Both are optional; the headers fall back
   * to what they said before when the call fails.
   */
  const health = rv?.headlineKpis ?? null;
  const evalPassRate = health?.aiQuality?.passRate ?? null;

  const jobRate = jb?.successRate24h ?? null;
  const jobTerminal = jb ? jb.succeededToday + jb.failedToday : null;

  /* ---------------------------------------------------------------- UH ---- */

  /* One denominator each, shared by the headline figure, the percentage and the
     split bar. `counts` and the usage endpoints each carry their own total, and
     reading a different one in each place let the page show "1,946 units" above
     a percentage worked out against a different number. Counts wins where it
     has an answer, because it is the figure the card displays. */
  const totalUnits = countOf(c, 'units') ?? uu?.summary?.totalUnits ?? null;
  const totalUsers = countOf(c, 'personnels') ?? ua?.summary?.totalUsers ?? null;
  const activeUnits = uu?.summary?.activeUnits ?? null;
  const activeUsers = ua?.summary?.activeUsers ?? null;

  /**
   * A share, or null when the two figures cannot form one.
   *
   * Clamping an over-100 result to "100%" hides a real disagreement between the
   * endpoints — the page would report 6,009 units active out of 1,946 and still
   * claim a tidy 100%. A share above the total is not a share, so it is dropped
   * and the caption's two raw numbers are left to speak for themselves. A
   * hair over 100 is rounding, and is clamped rather than discarded.
   */
  const shareOf = (part: number | null, whole: number | null): number | null => {
    if (part === null || whole === null || !(whole > 0) || part < 0) return null;
    const p = (part / whole) * 100;
    if (p > 100.1) return null;
    return Math.min(p, 100);
  };
  const unitPct = shareOf(activeUnits, totalUnits);
  const userPct = shareOf(activeUsers, totalUsers);
  const totalDistricts = countOf(c, 'districts');
  const userSeries = useMemo(() => {
    const t = ua?.activityTrend;
    if (!t?.length) return null;
    const stamps = t.map((p) => p.timestamp);
    return {
      values: t.map((p) => p.activeUsers),
      labels: axisLabels(stamps, withTime),
      stamps: axisLabels(stamps, withTime, t.length),
    };
  }, [ua, withTime]);

  const structure = useMemo(() => {
    const rows = [
      { nm: 'Ministries', n: data?.totals.ministries ?? null },
      { nm: 'Departments', n: countOf(c, 'departments') },
      { nm: 'Districts', n: countOf(c, 'districts') },
      { nm: 'Units', n: countOf(c, 'units') },
      { nm: 'Villages', n: countOf(c, 'unitVillages') },
      { nm: 'Users', n: countOf(c, 'personnels') ?? ua?.summary?.totalUsers ?? null },
    ].filter((r) => r.n !== null) as { nm: string; n: number }[];
    if (!rows.length) return null;
    /* Log scale: villages outrun ministries by three orders of magnitude, and a
       linear bar would leave every row but the last invisible. */
    const lmax = Math.log(Math.max(...rows.map((r) => r.n), 2)) || 1;
    /* The spread the caption reports, measured rather than asserted. It used to
       claim "villages outnumber ministries by three orders of magnitude", which
       is a statement about someone's sample data — with no villages recorded it
       is simply false. */
    const biggest = Math.max(...rows.map((r) => r.n));
    const smallest = Math.min(...rows.map((r) => r.n));
    return {
      rows: rows.map((r, i) => ({
        ...r, w: Math.max(5, (Math.log(Math.max(r.n, 1)) / lmax) * 100), colour: wheel(i),
      })),
      spread: smallest > 0 ? biggest / smallest : null,
    };
  }, [c, data, ua]);

  const ratios = useMemo(() => {
    const per = (a: number | null, b: number | null) =>
      a !== null && b !== null && b > 0 ? (a / b).toFixed(a / b < 10 ? 1 : 0) : null;
    const pairs: [string, number | null, number | null][] = [
      ['departments per ministry', countOf(c, 'departments'), data?.totals.ministries ?? null],
      ['units per department', countOf(c, 'units'), countOf(c, 'departments')],
      ['units per district', countOf(c, 'units'), countOf(c, 'districts')],
      ['villages per unit', countOf(c, 'unitVillages'), countOf(c, 'units')],
      ['users per unit', countOf(c, 'personnels'), countOf(c, 'units')],
    ];
    const out: { k: string; v: string }[] = [];
    for (const [k, a, b] of pairs) { const v = per(a, b); if (v) out.push({ k, v }); }
    return out;
  }, [c, data]);

  /* Districts rolled up from the busiest units. The unit-usage endpoint has no
     district aggregate, but every row carries its district — so this is a real
     rollup of real rows, and the panel says what it covers. */
  const districts = useMemo(() => {
    if (!uu?.topUnits?.length) return null;
    const by = new Map<string, { name: string; units: number; users: number; calls: number }>();
    for (const u of uu.topUnits) {
      const key = u.districtName ?? 'Unassigned';
      const row = by.get(key) ?? { name: key, units: 0, users: 0, calls: 0 };
      row.units += 1; row.users += u.activeUsers; row.calls += u.totalCalls;
      by.set(key, row);
    }
    const rows = [...by.values()].sort((a, b) => b.calls - a.calls).slice(0, 6);
    return rows.length ? rows : null;
  }, [uu]);

  const districtMax = districts?.length ? districts[0].calls : 0;
  /* The denominator the "share of calls" column claims: every call attributed
     to a unit in this window, not just the busiest row. */
  const unitCallTotal = uu?.summary?.totalCalls ?? null;

  /* Sentiment slices, plus whatever the three named moods do not account for —
     an item with no sentiment recorded still belongs to the total. */
  const fbSentiment = useMemo(() => {
    const o = fb?.overview;
    if (!o) return [];
    const pos = o.positive ?? 0, neu = o.neutral ?? 0, neg = o.negative ?? 0;
    const rest = Math.max((o.total ?? 0) - pos - neu - neg, 0);
    return [
      { name: 'Positive', v: pos, c: V.okFill },
      { name: 'Neutral', v: neu, c: V.ink4 },
      { name: 'Negative', v: neg, c: V.warnFill },
      { name: 'Unrated', v: rest, c: 'var(--pd-line-2)' },
    ].filter((r) => r.v > 0);
  }, [fb]);

  // -------------------------------------------------------------------------

  if (loading && !data) return <div className="pd-root"><DashboardSkeleton /></div>;

  const showTip = (title: string, rows: [string, string][], x: number, y: number) =>
    setTip({
      x, y, node: (
        <>
          <div className="t">{title}</div>
          {rows.map(([k, v]) => <div className="r" key={k}><span>{k}</span><b>{v}</b></div>)}
        </>
      ),
    });

  const stamp = data
    ? new Date(data.fetchedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';

  const viewMeta = (id: ViewId) =>
    id === 'ai' ? (ov ? `${compact(ov.currentPeriod?.totalPromptExecutions)} executions` : 'unavailable')
      : id === 'se' ? (errTotal !== null ? `${num(errTotal)} errors · ${num(sev?.critical ?? null)} critical` : 'unavailable')
        : (c ? `${num(totalUnits)} units · ${num(totalDistricts)} districts` : 'unavailable');

  const head = view === 'ai'
    ? (perf
        ? {
            tone: (perf.summary?.successRate ?? 0) >= OK_RATE ? '' : 'warn',
            text: [
              `${rate(perf.summary?.successRate)} of calls succeeded`,
              evalPassRate === null ? null : `${rate(passPct(evalPassRate))} eval pass`,
            ].filter(Boolean).join(' · '),
          }
        : { tone: 'idle', text: 'Performance unavailable' })
    : view === 'se'
      ? (sev || health
          ? {
              tone: (sev?.critical ?? 0) > 0 || health?.systemHealth === 'AT RISK' ? 'crit'
                : health?.systemHealth === 'FAIR' ? 'warn' : '',
              text: [
                health ? `${health.systemHealth} · ${health.healthyAreas}/${health.totalAreas} areas healthy` : null,
                sev ? (sev.critical > 0 ? `${num(sev.critical)} critical in ${period}` : `no critical errors in ${period}`) : null,
              ].filter(Boolean).join(' · '),
            }
          : { tone: 'idle', text: 'Error dashboard unavailable' })
      : (unitPct === null ? { tone: 'idle', text: 'Unit activity unavailable' }
        : { tone: '', text: `${Math.round(unitPct)}% of units active in ${period}` });

  return (
    <div className="pd-root" data-testid="SCR-PlatformDashboard">
      {/* ------------------------------------------------------------- top */}
      <div className="pd-top">
        <span className="t">Dashboard</span>
        <span className="s">{VIEWS.find((v) => v.id === view)?.subtitle}</span>
        <span className="grow" />
        <div className="pd-tabs">
          {RANGES.map((r) => (
            <button key={r.id} type="button" className={r.id === range ? 'on' : ''}
              aria-pressed={r.id === range} onClick={() => setRange(r.id)}
              data-testid={`PlatformDashboard.Range.${r.id}`}>{r.label}</button>
          ))}
        </div>
        <button type="button" className="pd-refresh" onClick={() => void load(range)} disabled={loading}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
            width="13" className={loading ? 'spin' : undefined} aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
          </svg>{loading ? 'Refreshing' : 'Refresh'}
        </button>
        {/* Left of the stamp, right of Refresh: the drill-down behind every
            figure on this page. Hidden without CROSS_APP.READ, which is what the
            API gates it on — a button that can only 403 is worse than none. */}
        {canExplore && (
          <button type="button" className="pd-explore" onClick={() => navigate('/data-explorer')}
            data-testid="PlatformDashboard.ExploreData" title="Browse every project's data">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round"
              strokeLinejoin="round" width="13" aria-hidden="true">
              <path d="M3 7V5a2 2 0 0 1 2-2h3l2 2h7a2 2 0 0 1 2 2v1" />
              <path d="M3 10h18l-1.6 8.2a2 2 0 0 1-2 1.8H6.6a2 2 0 0 1-2-1.8Z" />
            </svg>Explore Data
          </button>
        )}
        <span className="pd-stamp">{stamp}</span>
      </div>

      {/* --------------------------------------------------- module switch */}
      <div className="pd-views" role="tablist" aria-label="Dashboard module">
        {VIEWS.map((v) => (
          <button key={v.id} type="button" role="tab" aria-selected={v.id === view}
            className={`pd-view${v.id === view ? ' on' : ''}`} onClick={() => setView(v.id)}
            data-testid={`PlatformDashboard.View.${v.id}`}>
            <span className="badge">{v.code}</span>
            <span className="lb">{v.label}</span>
            <span className="mt">{viewMeta(v.id)}</span>
          </button>
        ))}
      </div>

      {data && data.unavailable.length > 0 && (
        <div className="pd-warn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" aria-hidden="true">
            <path d="M12 3 2.5 20h19L12 3ZM12 9v5M12 17h.01" />
          </svg>
          <span>Could not reach {data.unavailable.join(', ')}. Anything fed by{' '}
            {data.unavailable.length > 1 ? 'those endpoints' : 'that endpoint'} shows “—”, not zero.</span>
        </div>
      )}

      {/* ================================================================ AI */}
      {view === 'ai' && (
        <section className="pd-mod" key="ai">
          <header className="pd-mhead">
            <span className="code">AI</span>
            <span className={`status ${head.tone}`}><i />{head.text}</span>
            <span className="grow" />
            <span className="period">{period}</span>
            <button type="button" onClick={() => navigate('/prompts?tab=prompt-executions')}>Executions</button>
            <button type="button" onClick={() => navigate('/eval-runs')}>Eval runs</button>
          </header>

          <div className="pd-cells c6">
            <Cell k="Prompt executions" lead onClick={() => navigate('/prompts?tab=prompt-executions')}>
              <div className="row">
                <span className="v num">{compact(ov?.currentPeriod?.totalPromptExecutions)}</span>
                <Delta value={pct(ov?.change?.executions)} good="up" />
              </div>
              <span className="c">{ov ? `vs ${num(ov.previousPeriod?.totalPromptExecutions)} the period before` : 'no comparison available'}</span>
            </Cell>
            <Cell k="Call success">
              <div className="row">
                <span className="v num">{rate(perf?.summary?.successRate)}</span>
              </div>
              {perf ? <Split a={perf.summary?.successRate ?? 0} b={perf.summary?.errorRate ?? 0} />
                : <span className="c">performance not measured</span>}
            </Cell>
            <Cell k="Latency p95">
              <div className="row">
                <span className="v num">{secs(perf?.summary?.p95ResponseTime)}</span>
                {perf && <span className="pd-d flat">p50 {secs(perf.summary?.p50ResponseTime)}</span>}
              </div>
              <span className="c">{perf ? `slowest 5% · p99 ${secs(perf.summary?.p99ResponseTime)}` : 'performance not measured'}</span>
            </Cell>
            <Cell k="Tokens used">
              <div className="row">
                <span className="v num">{compact(ov?.currentPeriod?.totalTokensUsed)}</span>
                <Delta value={pct(ov?.change?.tokens)} good="up" />
              </div>
              <span className="c">{ov?.topModel ? `mostly ${ov.topModel}` : 'no model recorded'}</span>
            </Cell>
            <Cell k="Spend">
              <div className="row">
                <span className="v num">{money(ov?.currentPeriod?.totalCost)}</span>
                <Delta value={pct(ov?.change?.cost)} good="down" />
              </div>
              <span className="c">{ov ? `${money(ov.previousPeriod?.totalCost)} the period before` : 'no comparison available'}</span>
            </Cell>
            <Cell k="Models in use" end>
              <div className="pd-figure">
                <Donut pct={data?.totals.aiModels ? ((modelsUsed ?? 0) / data.totals.aiModels) * 100 : null} />
                <div className="meta">
                  <span className="v">{modelsUsed === null ? '—' : modelsUsed}<small> / {num(data?.totals.aiModels)}</small></span>
                  <span className="c">served a call in {period}</span>
                </div>
              </div>
            </Cell>
          </div>

          <div className="pd-cells c6 row2">
            <Cell k="Prompts defined" onClick={() => navigate('/prompts?tab=prompt-master')}>
              <span className="v sm num">{num(pa?.summary?.totalPrompts)}</span>
              <span className="c">{pa ? `${num(pa.summary?.activePrompts)} ran in ${period}` : 'unavailable'}</span>
            </Cell>
            <Cell k="Evals defined" onClick={() => navigate('/evals')}>
              <span className="v sm num">{num(data?.totals.evals)}</span>
              <span className="c">assertion suites on record</span>
            </Cell>
            <Cell k="Executions per user">
              <span className="v sm num">{execPerUser === null ? '—' : execPerUser.toFixed(1)}</span>
              <span className="c">{ov ? `across ${num(ov.currentPeriod?.activeUsers)} active users` : 'unavailable'}</span>
            </Cell>
            <Cell k="Busiest hour">
              <span className="v sm num">{peakHour ? `${String(peakHour.hour).padStart(2, '0')}:00` : '—'}</span>
              <span className="c">{peakHour ? `${num(peakHour.calls)} executions` : 'no hourly profile'}</span>
            </Cell>
            <Cell k="Avg response">
              <span className="v sm num">{secs(perf?.summary?.avgResponseTime)}</span>
              <span className="c">{pa ? `${num(pa.summary?.avgExecutionsPerPrompt)} runs per prompt` : 'unavailable'}</span>
            </Cell>
            <Cell k="Recent eval runs" end onClick={() => navigate('/eval-runs')}>
              <div className="row">
                <span className="v sm num">{evr ? evr.length : '—'}</span>
              </div>
              <span className="c">{evr ? 'most recent runs, newest first' : 'eval runs unavailable'}</span>
            </Cell>
          </div>

          <Strip title="Prompt activity"
            note={ov ? `${num(ov.currentPeriod?.totalPromptExecutions)} executions · ${rate(perf?.summary?.successRate)} success` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: 'minmax(0,1fr) 460px' }}>
            <Panel title="Executions by hour of day"
              right={peakHour ? `peak ${String(peakHour.hour).padStart(2, '0')}:00 · ${num(peakHour.calls)}` : undefined}>
              {aiSeries?.length ? (
                <Line series={aiSeries} id="pdAi" labels={HOUR_LABELS}
                  onHover={(i, x, y) => showTip(`${String(i).padStart(2, '0')}:00`, [
                    ['Executions', num(aiSeries[i])],
                    ['Avg response', secs(hourly?.byHour.get(i)?.avgResponseTime)],
                    ['Error rate', rate(hourly?.byHour.get(i)?.errorRate)],
                  ], x, y)}
                  onLeave={() => setTip(null)} />
              ) : <Empty what={perf ? 'No executions recorded in this window.' : 'Performance endpoint unavailable.'} />}
            </Panel>
            <Panel title={`Top prompts · ${period}`}>
              {pa?.topPrompts?.length ? (
                <>
                  <div className="pd-thead" style={{ gridTemplateColumns: 'minmax(0,1fr) 72px 56px 52px' }}>
                    <span>Prompt</span><span className="r">runs</span><span className="r">avg</span><span className="r">ok</span>
                  </div>
                  {pa.topPrompts.slice(0, 6).map((p) => (
                    <button key={p.promptId} type="button" className="pd-trow"
                      style={{ gridTemplateColumns: 'minmax(0,1fr) 72px 56px 52px' }}
                      onClick={() => navigate('/prompts?tab=prompt-master')}>
                      <span className="nm" title={p.promptName}>{p.promptName}</span>
                      <span className="n num">{num(p.totalExecutions)}</span>
                      <span className="n num">{secs(p.avgResponseTime)}</span>
                      <span className="n num" style={{ color: p.successRate >= OK_RATE ? V.ok : V.warn }}>{p.successRate.toFixed(0)}%</span>
                    </button>
                  ))}
                </>
              ) : <Empty what={pa ? 'No prompt executions in this window.' : 'Prompt analytics unavailable.'} />}
            </Panel>
          </div>

          <Strip title="Models & evals"
            note={modelsUsed !== null ? `${modelsUsed} of ${num(data?.totals.aiModels)} models served calls` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
            <Panel title="Executions by model">
              {perf?.byModel?.length ? (
                <div className="pd-rows">
                  {perf.byModel.slice(0, 6).map((m, i) => (
                    <Bar key={m.model} name={<span className="mono">{m.model || 'Unknown'}</span>} value={num(m.calls)}
                      width={modelMax ? (m.calls / modelMax) * 100 : 0} colour={wheel(i)} nameW={124} />
                  ))}
                </div>
              ) : <Empty what={perf ? 'No model recorded any calls.' : 'Performance endpoint unavailable.'} />}
            </Panel>
            <Panel title="Model latency" right="avg · success">
              {perf?.byModel?.length ? (
                <>
                  <div className="pd-thead" style={{ gridTemplateColumns: 'minmax(0,1fr) 52px 56px' }}>
                    <span>Model</span><span className="r">avg</span><span className="r">success</span>
                  </div>
                  {perf.byModel.slice(0, 6).map((m) => (
                    <div key={m.model} className="pd-trow static" style={{ gridTemplateColumns: 'minmax(0,1fr) 52px 56px' }}>
                      <span className="nm mono">{m.model || 'Unknown'}</span>
                      <span className="n num">{secs(m.avgResponseTime)}</span>
                      <span className="n num" style={{ color: m.successRate >= OK_RATE ? undefined : V.warn }}>{m.successRate.toFixed(0)}%</span>
                    </div>
                  ))}
                  <p className="pd-note">Percentiles are not broken out per model — the endpoint reports the mean only.</p>
                </>
              ) : <Empty what={perf ? 'No per-model timings recorded.' : 'Performance endpoint unavailable.'} />}
            </Panel>
            <Panel title="Prompt mix by type" right="share of executions">
              {pa?.byType?.length ? (
                <div className="pd-rows">
                  {pa.byType.slice(0, 6).map((t, i) => (
                    <Bar key={t.promptType} name={titleCase(t.promptType || 'Untyped')}
                      value={rate(t.percentage, t.percentage < 10 ? 1 : 0)}
                      sub={`${num(t.totalExecutions)} runs · ${num(t.count)} prompts`}
                      width={t.percentage} colour={wheel(i)} nameW={98} valW={46} />
                  ))}
                </div>
              ) : <Empty what={pa ? 'No prompt types recorded in this window.' : 'Prompt analytics unavailable.'} />}
            </Panel>
          </div>

          <Strip title="Latency profile"
            note={perf ? `avg ${secs(perf.summary?.avgResponseTime)} · p95 ${secs(perf.summary?.p95ResponseTime)}` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: '300px minmax(0,1fr)' }}>
            <Panel title="Response time distribution">
              {perf?.responseTimeDistribution?.length ? (
                <div className="pd-rows">
                  {perf.responseTimeDistribution.map((b, i) => (
                    <Bar key={b.range} name={b.range} value={`${b.percentage.toFixed(1)}%`}
                      sub={`${num(b.count)} calls`} width={latMax ? (b.count / latMax) * 100 : 0}
                      colour={wheel(i)} nameW={64} valW={48} />
                  ))}
                </div>
              ) : <Empty what={perf ? 'No latency buckets recorded.' : 'Performance endpoint unavailable.'} />}
            </Panel>
            <Panel title="Slowest calls" right={`longest executions in ${period}`}>
              {perf?.slowestCalls?.length ? (
                <>
                  <div className="pd-thead" style={{ gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr) minmax(0,.8fr) 66px 58px 52px' }}>
                    <span>Prompt</span><span>model</span><span>user</span>
                    <span className="r">tokens</span><span className="r">time</span><span className="r">when</span>
                  </div>
                  {perf.slowestCalls.slice(0, 6).map((s) => (
                    <button key={s.executionId} type="button" className="pd-trow"
                      style={{ gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr) minmax(0,.8fr) 66px 58px 52px' }}
                      onClick={() => navigate('/prompts?tab=prompt-executions')}>
                      <span className="nm">{s.promptName ?? '—'}</span>
                      <span className="nm mono dim">{s.model ?? '—'}</span>
                      <span className="nm dim">{s.userName ?? '—'}</span>
                      <span className="n num">{num(s.tokens)}</span>
                      <span className="n num" style={{ color: V.warn, fontWeight: 500 }}>{secs(s.responseTime)}</span>
                      <span className="n num dim">{ago(s.timestamp)}</span>
                    </button>
                  ))}
                </>
              ) : <Empty what={perf ? 'No executions to rank in this window.' : 'Performance endpoint unavailable.'} />}
            </Panel>
          </div>

          <Strip title="Eval runs"
            note={evr ? `${evr.length} most recent · ${evalBelow ?? 0} below their suite's gate` : undefined} />
          <div className="pd-grid">
            <Panel title="Recent eval runs">
              {evr?.length ? (
                <>
                  <div className="pd-thead" style={{ gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr) 70px 80px 80px 96px 56px' }}>
                    <span>Eval</span><span>model</span><span className="r">cases</span><span className="r">pass rate</span>
                    <span className="r">duration</span><span className="r">status</span><span className="r">when</span>
                  </div>
                  {evr.map((r) => {
                    const pr = passPct(r.summary?.passRate);
                    const below = belowGate(r);
                    const text = r.status === 'completed' ? (below ? 'Below gate' : 'Passed') : titleCase(r.status);
                    const tone = r.status === 'failed' || below ? V.bad : r.status === 'completed' ? V.ok : V.accent;
                    return (
                      <button key={r._id ?? r.evalRunId} type="button" className="pd-trow"
                        style={{ gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr) 70px 80px 80px 96px 56px' }}
                        onClick={() => navigate('/eval-runs')}>
                        <span className="nm">{r.purpose || r.suiteCode || r.suiteRef || r.evalRunId || 'Eval run'}</span>
                        <span className="nm mono dim">{r.modelId ?? '—'}</span>
                        <span className="n num">{num(r.summary?.total)}</span>
                        <span className="n num">{pr === null ? '—' : rate(pr)}</span>
                        <span className="n num">{dur(r.summary?.durationSeconds)}</span>
                        <span className="n num" style={{ color: tone, fontWeight: 500 }}>{text}</span>
                        <span className="n num dim">{ago(r.runDateTime ?? r.createdAt)}</span>
                      </button>
                    );
                  })}
                </>
              ) : <Empty what={evr ? 'No eval runs recorded yet.' : 'Eval runs unavailable.'} />}
            </Panel>
          </div>
        </section>
      )}

      {/* ================================================================ SE */}
      {view === 'se' && (
        <section className="pd-mod" key="se">
          <header className="pd-mhead">
            <span className="code">Software Engineering</span>
            <span className={`status ${head.tone}`}><i />{head.text}</span>
            <span className="grow" />
            <span className="period">{period}</span>
            <button type="button" onClick={() => navigate('/logs?tab=error-logs')}>Error logs</button>
            <button type="button" onClick={() => navigate('/background-jobs')}>Jobs</button>
            <button type="button" onClick={() => navigate('/logs?tab=transaction')}>Audit log</button>
          </header>

          <div className="pd-cells c6">
            <Cell k="Errors" lead onClick={() => navigate('/logs?tab=error-logs')}>
              <div className="row">
                <span className="v num">{num(errTotal)}</span>
                <Delta value={pct(errDelta)} good="down" />
              </div>
              {sev && (
                <div className="pd-split micro">
                  <span style={{ flex: Math.max(sev.critical, .001), background: 'var(--pd-bad)' }} />
                  <span style={{ flex: Math.max(sev.high, .001), background: V.badFill }} />
                  <span style={{ flex: Math.max(sev.medium, .001), background: V.warnFill }} />
                  <span style={{ flex: Math.max(sev.low + sev.other, .001), background: V.okFill }} />
                </div>
              )}
              <span className="c">
                {el
                  ? `${num(el.summary?.totalUniqueErrors)} distinct${errDelta === null ? '' : ' · trend is 2nd half of the window vs 1st'}`
                  : 'error dashboard unavailable'}
              </span>
            </Cell>
            <Cell k="Critical" onClick={() => navigate('/logs?tab=error-logs')}>
              <div className="row">
                <span className="v num" style={{ color: sev && sev.critical > 0 ? V.bad : undefined }}>{num(sev?.critical ?? null)}</span>
                {el?.impact?.criticalErrorRate ? <span className="pd-d bad">{rate(el.impact.criticalErrorRate)} of all</span> : null}
              </div>
              <span className="c">{el ? `${num(el.summary?.totalAffectedUsers)} users affected overall` : 'unavailable'}</span>
            </Cell>
            <Cell k="Log error rate">
              <span className="v num">{errRate === null ? '—' : `${errRate.toFixed(2)}%`}</span>
              {/* Names the denominator errRate actually divides by — audit
                  events in the same window, not AI prompt executions. */}
              <span className="c">
                {errRate === null ? 'no levelled log volume' : `error-level share of ${compact(logLevels?.total)} events`}
              </span>
            </Cell>
            <Cell k="Audit events" onClick={() => navigate('/logs?tab=transaction')}>
              <span className="v num">{compact(au?.overview?.totalLogs)}</span>
              <span className="c">{au ? `${num(au.overview?.todayLogs)} today · ${num(au.overview?.weekLogs)} this week` : 'audit dashboard unavailable'}</span>
            </Cell>
            <Cell k="Notifications">
              <div className="row">
                <span className="v num">{compact(nt?.totalNotifications)}</span>
                {nt && nt.totalFailed > 0 && <span className="pd-d warn">{num(nt.totalFailed)} failed</span>}
              </div>
              <span className="c">{nt ? `${rate(nt.overallSuccessRate)} delivered` : 'notification analytics unavailable'}</span>
            </Cell>
            <Cell k="Job success" end onClick={() => navigate('/background-jobs')}>
              <div className="row">
                <span className="v num">{jobRate === null ? '—' : rate(jobRate)}</span>
                {jb && jb.runningJobs > 0 && <span className="pd-d flat">{jb.runningJobs} running</span>}
              </div>
              <span className="c">{jb ? (jobRate === null ? 'nothing finished in 24h' : `over ${num(jb.runsLast24h)} runs in 24h`) : 'background jobs unavailable'}</span>
            </Cell>
          </div>

          <Strip title="Errors"
            note={el ? `${num(errTotal)} in ${period} · ${num(el.totalErrorGroups ?? el.errorGroups?.length ?? null)} distinct groups` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: 'minmax(0,1.35fr) minmax(0,.95fr) minmax(0,1.2fr)' }}>
            <Panel title="Volume over time"
              right={errTimeline?.length ? `${errTimeline[0].label} → ${errTimeline[errTimeline.length - 1].label}` : undefined}>
              {errTimeline?.length ? (
                <>
                  <div className="pd-stack" role="img" aria-label={`Errors per bucket: ${errTimeline.map((b) => b.total).join(', ')}`}>
                    {(() => {
                      const max = Math.max(...errTimeline.map((b) => b.total), 1);
                      return errTimeline.map((b, i) => {
                        const crit = sevAt(b.bySeverity, 'critical', 'fatal', 'blocker');
                        const high = sevAt(b.bySeverity, 'high', 'error', 'major');
                        const rest = Math.max(b.total - crit - high, 0);
                        return (
                          <div key={i} className="col" style={{ height: `${Math.max(6, (b.total / max) * 100)}%` }}
                            onMouseEnter={(e) => showTip(b.label, [['All errors', num(b.total)], ['Critical', num(crit)], ['High', num(high)]], e.clientX, e.clientY)}
                            onMouseLeave={() => setTip(null)}>
                            <span style={{ flex: Math.max(crit, .001), background: 'var(--pd-bad)' }} />
                            <span style={{ flex: Math.max(high, .001), background: V.badFill }} />
                            <span style={{ flex: Math.max(rest, .001), background: V.mid }} />
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <div className="pd-legend">
                    <span><i style={{ background: 'var(--pd-bad)' }} />Critical</span>
                    <span><i style={{ background: V.badFill }} />High</span>
                    <span><i style={{ background: V.mid }} />Lower</span>
                    <span className="grow" />
                    <span>peak {num(Math.max(...errTimeline.map((b) => b.total)))}</span>
                  </div>
                </>
              ) : <Empty what={el ? 'No errors recorded in this window.' : 'Error dashboard unavailable.'} />}
            </Panel>

            <Panel title="Severity mix">
              {sevRows.length ? (
                <>
                  <div className="pd-figure">
                    <Ring parts={sevRows.map((r) => ({ v: r.v, c: r.c }))} />
                    <div className="meta rows">
                      {sevRows.map((r) => (
                        <div key={r.name} className="pd-legrow">
                          <i style={{ background: r.c }} />
                          <span className="nm">{r.name}</span>
                          <span className="n num">{num(r.v)}</span>
                          <span className="p num">{((r.v / sev!.total) * 100).toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="pd-note">
                    {errDelta === null ? 'Not enough buckets to state a direction.'
                      : errDelta < 0 ? `Falling — down ${Math.abs(errDelta).toFixed(0)}% across the window.`
                        : `Rising — up ${errDelta.toFixed(0)}% across the window.`}
                  </p>
                </>
              ) : <Empty what={el ? 'No errors to break down.' : 'Error dashboard unavailable.'} />}
            </Panel>

            <Panel title="Open error groups" right="count · last seen">
              {el?.errorGroups?.length ? (() => {
                const max = Math.max(...el.errorGroups.map((x) => x.occurrences), 1);
                return el.errorGroups.slice(0, 5).map((g, i) => {
                  const t = sevTone(g.severity);
                  return (
                    <button key={`${g.errorCode}-${i}`} type="button" className="pd-group"
                      onClick={() => navigate('/logs?tab=error-logs')} title={g.resolvedMessage ?? g.message ?? undefined}>
                      <div className="hd">
                        <span className="pd-sev" style={chip(t.f)}>{(g.severity || 'LOW').slice(0, 4).toUpperCase()}</span>
                        <span className="nm">{g.sourceName || g.errorCode}</span>
                        <span className="n num">{num(g.occurrences)}</span>
                        <span className="a num">{ago(g.lastSeen)}</span>
                      </div>
                      <span className="track"><i style={{ width: `${Math.max(4, (g.occurrences / max) * 100)}%`, background: t.f }} /></span>
                    </button>
                  );
                });
              })() : <Empty what={el ? 'No error groups in this window.' : 'Error dashboard unavailable.'} />}
            </Panel>
          </div>

          <div className="pd-grid" style={{ gridTemplateColumns: '300px minmax(0,1fr)', borderTop: '1px solid var(--pd-line)' }}>
            <Panel title="Errors by source" right="count">
              {errByType ? (
                <div className="pd-rows">
                  {(() => { const max = errByType[0].count;
                    return errByType.slice(0, 5).map((r, i) => (
                      <Bar key={r.name} name={r.name} value={num(r.count)}
                        sub={`${num(r.unique)} distinct · ${num(r.users)} users`}
                        width={(r.count / max) * 100} colour={wheel(i)} nameW={84} />
                    )); })()}
                </div>
              ) : <Empty what={el ? 'No errors to group by source.' : 'Error dashboard unavailable.'} />}
            </Panel>
            <Panel title="Errors by hour of day" right="every day folded onto a 24-hour clock">
              {errByHour?.length ? (
                <Line series={errByHour} id="pdErrH" labels={HOUR_LABELS}
                  onHover={(i, x, y) => showTip(`${String(i).padStart(2, '0')}:00`, [['Errors', num(errByHour[i])]], x, y)}
                  onLeave={() => setTip(null)} />
              ) : <Empty what={el ? 'No hourly error profile recorded.' : 'Error dashboard unavailable.'} />}
            </Panel>
          </div>

          <Strip title="Logs" note={au?.topEndpoints?.length ? `busiest endpoint ${au.topEndpoints[0].endpoint}` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: '260px minmax(0,1fr) minmax(0,1fr)' }}>
            <Panel title={`Ingested ${period}`}>
              {au ? (
                <>
                  <span className="v num" style={{ font: '400 22px var(--pd-mono)', color: 'var(--pd-ink)', letterSpacing: '-.025em' }}>
                    {compact(au.overview?.totalLogs)}
                  </span>
                  <span className="c" style={{ font: '400 11px inherit', color: 'var(--pd-ink-4)' }}>
                    {num(au.overview?.todayLogs)} today · {num(au.overview?.weekLogs)} this week
                  </span>
                  {auditSeries ? (
                    <Line series={auditSeries.values} id="pdAudit" labels={auditSeries.labels}
                      onHover={(i, x, y) => showTip(auditSeries.stamps[i], [['Events', num(auditSeries.values[i])]], x, y)}
                      onLeave={() => setTip(null)} />
                  ) : null}
                  {au.templateHealth && (
                    <p className="pd-note">
                      {num(au.templateHealth.activeWithLogs)} of {num(au.templateHealth.total)} log templates firing ·{' '}
                      {num(au.templateHealth.activeNoLogs)} active but silent.
                    </p>
                  )}
                </>
              ) : <Empty what="Audit dashboard unavailable." />}
            </Panel>
            <Panel title="Volume by level">
              {logLevels ? (
                <>
                  <div className="pd-ribbon">
                    {logLevels.rows.map((r) => (
                      <span key={r.name} style={{ flex: Math.max(r.v, .001), background: r.c }} title={`${r.name}: ${r.v}`} />
                    ))}
                  </div>
                  <div className="pd-legend">
                    {logLevels.rows.map((r) => (
                      <span key={r.name}><i style={{ background: r.c }} />{r.name}<b className="num">{num(r.v)}</b></span>
                    ))}
                  </div>
                  <div className="pd-thead" style={{ gridTemplateColumns: 'minmax(0,1fr) 64px', marginTop: 2 }}>
                    <span>By layer</span><span className="r">events</span>
                  </div>
                  <div className="pd-rows" style={{ gap: 6 }}>
                    {byLayer.slice(0, 4).map(([k, v], i) => (
                      <Bar key={k} name={label(k)} value={num(v)}
                        width={layerMax ? (v / layerMax) * 100 : 0} colour={wheel(i)} nameW={78} />
                    ))}
                  </div>
                </>
              ) : <Empty what={au ? 'No levelled log volume recorded.' : 'Audit dashboard unavailable.'} />}
            </Panel>
            <Panel title="Busiest endpoints" right="by audit events">
              {au?.topEndpoints?.length ? (
                <div className="pd-rows">
                  {au.topEndpoints.slice(0, 6).map((e, i) => (
                    <Bar key={e.endpoint} name={<span className="mono">{e.endpoint}</span>} value={num(e.count)}
                      width={endpointMax ? (e.count / endpointMax) * 100 : 0} colour={wheel(i)} nameW={150} />
                  ))}
                </div>
              ) : <Empty what={au ? 'No endpoint ranking recorded.' : 'Audit dashboard unavailable.'} />}
            </Panel>
          </div>

          <Strip title="Notifications"
            note={nt ? `${num(nt.totalNotifications)} sent · ${num(nt.totalDelivered)} delivered · ${num(nt.totalFailed)} failed` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr) minmax(0,1fr)' }}>
            <Panel title="Delivery by channel" right="% delivered">
              {nt?.channelStats?.length ? (() => {
                // Busiest channel first; the bar fills to the delivery rate, the
                // number beside it is the volume.
                const rows = [...nt.channelStats].sort((a, b) => b.total - a.total);
                return (
                  <div className="pd-rows">
                    {rows.map((ch) => (
                      <Bar key={ch.channel} name={label(ch.channel)} value={num(ch.total)}
                        sub={<><b style={{ color: ch.successRate >= OK_DELIVERY_RATE ? V.ok : V.warn }}>
                          {rate(ch.successRate, ch.successRate >= 99.95 ? 0 : 1)} delivered</b>
                          {ch.failed > 0 && <> · {num(ch.failed)} failed</>}
                          {(ch.noDevice ?? 0) > 0 && <> · {num(ch.noDevice ?? 0)} no device</>}
                          {ch.pending > 0 && <> · {num(ch.pending)} pending</>}</>}
                        width={ch.successRate}
                        colour={ch.successRate >= OK_DELIVERY_RATE ? V.okFill : V.warnFill} nameW={62} valW={62} />
                    ))}
                  </div>
                );
              })() : <Empty what={nt ? 'No notifications sent in this window.' : 'Notification analytics unavailable.'} />}
            </Panel>
            <Panel title="Delivery outcome">
              {nt ? (
                <>
                  <div className="pd-quad">
                    <div><span className="v num">{compact(nt.totalNotifications)}</span><span className="c">sent</span></div>
                    <div><span className="v num" style={{ color: V.ok }}>{rate(nt.overallSuccessRate)}</span><span className="c">delivered</span></div>
                    <div><span className="v num" style={{ color: nt.totalFailed > 0 ? V.warn : undefined }}>{num(nt.totalFailed)}</span><span className="c">failed</span></div>
                    <div>
                      <span className="v num">{num(nt.channelStats.reduce((n, ch) => n + (ch.pending ?? 0), 0))}</span>
                      <span className="c">still pending</span>
                    </div>
                  </div>
                  <span className="pd-ribbon thin">
                    <span style={{ flex: Math.max(nt.overallSuccessRate, .001), background: V.okFill }} />
                    <span style={{ flex: Math.max(100 - nt.overallSuccessRate, .001), background: 'rgba(15,23,42,.10)' }} />
                  </span>
                  {notifSeries ? (
                    <Line series={notifSeries.values} id="pdNotif" labels={notifSeries.labels}
                      onHover={(i, x, y) => showTip(notifSeries.stamps[i], [
                        ['Sent', num(notifSeries.values[i])],
                        ['Delivered', num(notifSeries.delivered[i])],
                        ['Failed', num(notifSeries.failed[i])],
                      ], x, y)}
                      onLeave={() => setTip(null)} />
                  ) : null}
                </>
              ) : <Empty what="Notification analytics unavailable." />}
            </Panel>
            <Panel title="Retry profile & templates">
              {nt?.retryAnalysis?.length || nt?.topTemplates?.length ? (
                <div className="pd-rows">
                  {nt.retryAnalysis?.length ? (() => {
                    const max = Math.max(...nt.retryAnalysis.map((r) => r.count), 1);
                    return nt.retryAnalysis.slice(0, 3).map((r, i) => (
                      <Bar key={r.label} name={titleCase(r.label)} value={num(r.count)}
                        sub={`${r.percentage.toFixed(1)}% of messages`}
                        width={(r.count / max) * 100} colour={wheel(i)} nameW={92} />
                    ));
                  })() : null}
                  {nt.topTemplates?.length ? (
                    <>
                      <div className="pd-thead" style={{ gridTemplateColumns: 'minmax(0,1fr) 64px', marginTop: 2 }}>
                        <span>Top templates</span><span className="r">sent</span>
                      </div>
                      {nt.topTemplates.slice(0, 3).map((t, i) => (
                        <Bar key={t.notificationType} name={t.name || titleCase(t.notificationType)} value={num(t.count)}
                          width={templateMax ? (t.count / templateMax) * 100 : 0} colour={wheel(i + 3)} nameW={126} />
                      ))}
                    </>
                  ) : null}
                </div>
              ) : <Empty what={nt ? 'No retry or template data in this window.' : 'Notification analytics unavailable.'} />}
            </Panel>
          </div>

          <Strip title="Audit logs"
            note={au?.topUsers?.length ? `${num(au.topUsers.length)} busiest actors · top ${au.topUsers[0].name || au.topUsers[0].actorId}` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: 'minmax(0,1fr) 260px' }}>
            <Panel title="Recent activity">
              {au?.logs?.items?.length ? au.logs.items.slice(0, 6).map((l) => (
                <div key={l._id} className="pd-feed">
                  <i />
                  <span className="ac">{l.actorName || l.actorId || 'system'}</span>
                  <span className="tx" title={l.message ?? l.logCode}>{l.message || l.logCode}</span>
                  <span className="ag">{ago(l.EventTimeStamp ?? l.createdAt)}</span>
                </div>
              )) : <Empty what={au ? 'No audit events in this window.' : 'Audit dashboard unavailable.'} />}
            </Panel>
            <Panel title="Busiest actors" right="events">
              {au?.topUsers?.length ? (
                <div className="pd-rows">
                  {(() => { const max = au.topUsers[0].count || 1;
                    return au.topUsers.slice(0, 5).map((u, i) => (
                      <Bar key={u.actorId} name={u.name || u.actorId} value={num(u.count)}
                        width={(u.count / max) * 100} colour={wheel(i)} nameW={92} />
                    )); })()}
                </div>
              ) : <Empty what={au ? 'No actor ranking recorded.' : 'Audit dashboard unavailable.'} />}
            </Panel>
          </div>

          <Strip title="Feedback"
            note={fb ? `${num(fb.overview?.total)} items ${period}${typeof fb.overview?.averageRating === 'number' ? ` · avg ${fb.overview.averageRating.toFixed(1)}` : ''}` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: '260px minmax(0,1fr)' }}>
            <Panel title="Items received">
              {fb ? (
                <>
                  <div className="row" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="v num" style={{ font: '400 22px var(--pd-mono)', color: V.accent, letterSpacing: '-.03em' }}>
                      {num(fb.overview?.total)}
                    </span>
                    <span className="grow" />
                    {fb.trend && fb.trend.length > 1 && <Spark series={fb.trend.map((t) => t.count)} />}
                  </div>
                  {/* Any item carrying no sentiment gets its own slice, so the
                      ribbon spans the whole total rather than implying the three
                      named moods account for every item received. */}
                  <span className="pd-ribbon thin">
                    {fbSentiment.map((r) => (
                      <span key={r.name} style={{ flex: Math.max(r.v, .001), background: r.c }} title={`${r.name}: ${r.v}`} />
                    ))}
                  </span>
                  <div className="pd-legend">
                    {fbSentiment.map((r) => (
                      <span key={r.name}><i style={{ background: r.c }} />{r.name}<b className="num">{num(r.v)}</b></span>
                    ))}
                  </div>
                </>
              ) : <Empty what="Feedback dashboard unavailable." />}
            </Panel>
            <Panel title="By target type">
              {fb?.byTargetType?.length ? (
                <div className="pd-rows">
                  {(() => { const max = Math.max(...fb.byTargetType.map((t) => t.count), 1);
                    return fb.byTargetType.slice(0, 6).map((t, i) => (
                      <Bar key={t.targetType} name={titleCase(t.targetType)} value={num(t.count)}
                        width={(t.count / max) * 100} colour={wheel(i)} nameW={132} />
                    )); })()}
                </div>
              ) : <Empty what={fb ? 'No feedback recorded in this window.' : 'Feedback dashboard unavailable.'} />}
            </Panel>
          </div>

          <Strip title="Background jobs"
            note={jb ? `${num(jb.activeJobs)} of ${num(jb.totalJobs)} enabled · ${num(jb.enabledSchedules)} schedules live${jb.nextRunAt ? ` · next fire ${until(jb.nextRunAt)}` : ''}` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: '260px minmax(0,1fr)' }}>
            <Panel title="Last 24 hours">
              {jb ? (
                <>
                  <div className="pd-figure">
                    <Donut pct={jobRate} colour={V.okFill} size={52} />
                    <div className="meta">
                      <span className="v">{jobRate === null ? '—' : rate(jobRate)}</span>
                      {/* The rate is `successRate24h`, so its denominator has to be
                          `runsLast24h`. It used to sit beside succeeded+failed
                          TODAY, which is a different window entirely. */}
                      <span className="c">
                        {jobRate === null ? 'no runs finished in 24h' : `of ${num(jb.runsLast24h)} runs in 24h`}
                      </span>
                    </div>
                  </div>
                  <div className="pd-quad two">
                    <div>
                      <span className="v num" style={{ color: jb.failedToday > 0 ? V.warn : undefined }}>{num(jb.failedToday)}</span>
                      <span className="c">failed today{jobTerminal ? ` of ${num(jobTerminal)}` : ''}</span>
                    </div>
                    <div><span className="v num">{num(jb.runningJobs)}</span><span className="c">running now</span></div>
                  </div>
                </>
              ) : <Empty what="Background jobs unavailable." />}
            </Panel>
            <Panel title="Recent runs">
              {runs?.length ? (
                <>
                  <div className="pd-thead" style={{ gridTemplateColumns: 'minmax(0,1.7fr) minmax(0,1fr) 64px 72px 82px' }}>
                    <span>Job</span><span>triggered by</span><span className="r">attempt</span>
                    <span className="r">started</span><span className="r">status</span>
                  </div>
                  {runs.map((r) => {
                    const bad = /FAIL|ERROR/i.test(r.status);
                    const live = /RUN|PEND/i.test(r.status);
                    return (
                      <button key={r._id} type="button" className="pd-trow"
                        style={{ gridTemplateColumns: 'minmax(0,1.7fr) minmax(0,1fr) 64px 72px 82px' }}
                        onClick={() => navigate('/background-jobs')}>
                        <span className="nm">{r.jobName || jobNameById.get(r.jobId) || r.jobKey || 'Job run'}</span>
                        <span className="nm mono dim">{titleCase(r.triggeredBy || '—')}</span>
                        <span className="n num">#{r.attemptNumber}</span>
                        <span className="n num dim">{ago(r.startedAt)}</span>
                        <span className="n num" style={{ color: bad ? V.bad : live ? V.accent : V.ok, fontWeight: 500 }}>
                          {titleCase(r.status)}
                        </span>
                      </button>
                    );
                  })}
                </>
              ) : <Empty what={runs ? 'No runs recorded yet.' : 'Job runs unavailable.'} />}
            </Panel>
          </div>
        </section>
      )}

      {/* ================================================================ UH */}
      {view === 'uh' && (
        <section className="pd-mod" key="uh">
          <header className="pd-mhead">
            <span className="code">Unit Hierarchy</span>
            <span className={`status ${head.tone}`}><i />{head.text}</span>
            <span className="grow" />
            <span className="period">structure is all-time</span>
            <button type="button" onClick={() => navigate('/units?tab=org-structure')}>Org structure</button>
            <button type="button" onClick={() => navigate('/units?tab=units')}>Unit master</button>
          </header>

          <div className="pd-cells c6">
            <Cell k="Total units" lead onClick={() => navigate('/units?tab=units')}>
              <span className="v num">{compact(totalUnits)}</span>
              <span className="c">{activeUnits !== null ? `${num(activeUnits)} active in ${period}` : 'the operational level'}</span>
            </Cell>
            <Cell k="Unit types" onClick={() => navigate('/units?tab=unit-types')}>
              <span className="v num">{num(countOf(c, 'unitTypes'))}</span>
              <span className="c">classifications in use</span>
            </Cell>
            <Cell k="Unit villages" onClick={() => navigate('/units?tab=unit-villages')}>
              <span className="v num">{compact(countOf(c, 'unitVillages'))}</span>
              <span className="c">village-to-unit assignments</span>
            </Cell>
            <Cell k="Ministries" onClick={() => navigate('/organization?tab=ministries')}>
              <span className="v num">{num(data?.totals.ministries)}</span>
              <span className="c">root of the tree</span>
            </Cell>
            <Cell k="Departments" onClick={() => navigate('/organization?tab=departments')}>
              <span className="v num">{num(countOf(c, 'departments'))}</span>
              <span className="c">one level below ministries</span>
            </Cell>
            <Cell k="Districts" end onClick={() => navigate('/organization?tab=districts')}>
              <span className="v num">{num(totalDistricts)}</span>
              <span className="c">the geographic split</span>
            </Cell>
          </div>

          <div className="pd-cells c4 row2">
            <Cell k="Unique users logged in">
              <div className="row">
                <span className="v sm num">{compact(activeUsers)}</span>
                <span className="grow" />
                {userSeries && userSeries.values.length > 1 && <Spark series={userSeries.values} />}
              </div>
              <span className="c">{ua ? `of ${num(totalUsers)} registered · ${userPct === null ? '—' : `${Math.round(userPct)}%`}` : 'user activity unavailable'}</span>
            </Cell>
            <Cell k="New users">
              <span className="v sm num" style={{ color: V.ok }}>{num(ua?.summary?.newUsers)}</span>
              <span className="c">{ua ? `first sign-in in ${period}` : 'unavailable'}</span>
            </Cell>
            <Cell k="Units with activity">
              <div className="row">
                <span className="v sm num">{compact(activeUnits)}</span>
                {unitPct !== null && <span className="pd-d flat">{Math.round(unitPct)}%</span>}
              </div>
              {unitPct !== null && activeUnits !== null && totalUnits !== null
                ? <Split a={activeUnits} b={Math.max(totalUnits - activeUnits, 0)} />
                : <span className="c">
                    {activeUnits === null ? 'unit usage unavailable' : `of ${num(totalUnits)} on record`}
                  </span>}
            </Cell>
            <Cell k="Calls from units" end>
              <span className="v sm num">{compact(uu?.summary?.totalCalls)}</span>
              <span className="c">{uu ? `${money(uu.summary?.totalCost)} attributed spend` : 'unavailable'}</span>
            </Cell>
          </div>

          <Strip title="Structure"
            note={c ? `${num(totalUnits)} units · ${num(countOf(c, 'unitVillages'))} villages · ${num(countOf(c, 'departments'))} departments` : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: 'minmax(0,1fr) 460px' }}>
            <Panel title="Levels of the hierarchy" right="log scale">
              {structure ? (
                <div className="pd-rows">
                  {structure.rows.map((r) => (
                    <Bar key={r.nm} name={r.nm} value={r.n.toLocaleString()} width={r.w} colour={r.colour} nameW={92} valW={62} />
                  ))}
                  <p className="pd-note">
                    Bars are log-scaled{structure.spread && structure.spread >= 10
                      ? ` — the largest level is ${compact(Math.round(structure.spread))}\u00d7 the smallest`
                      : ''}.
                  </p>
                </div>
              ) : <Empty what="Counts endpoint unavailable." />}
            </Panel>
            <Panel title="Average fan-out" right="each level ÷ the level above">
              {ratios.length ? (
                <div className="pd-ratios">
                  {ratios.map((r) => (
                    <div key={r.k}><span className="v3 num">{r.v}</span><span className="k3">{r.k}</span></div>
                  ))}
                </div>
              ) : <Empty what="Counts endpoint unavailable — no ratios to derive." />}
            </Panel>
          </div>

          <Strip title="Activity"
            note={activeUsers !== null && activeUnits !== null
              ? `${num(activeUsers)} of ${num(totalUsers)} users · ${num(activeUnits)} of ${num(totalUnits)} units active in ${period}`
              : undefined} />
          <div className="pd-grid" style={{ gridTemplateColumns: 'minmax(0,1fr) 460px' }}>
            <Panel title={`Users signed in · ${period}`}
              right={ua ? `${num(ua.summary?.newUsers)} new · ${userPct === null ? '—' : `${Math.round(userPct)}% of registered`}` : undefined}>
              {userSeries ? (
                <Line series={userSeries.values} id="pdUsers" labels={userSeries.labels}
                  onHover={(i, x, y) => showTip(userSeries.stamps[i], [
                    ['Users signed in', num(userSeries.values[i])],
                    ['New users', num(ua?.activityTrend?.[i]?.newUsers)],
                    ['Calls', num(ua?.activityTrend?.[i]?.totalCalls)],
                  ], x, y)}
                  onLeave={() => setTip(null)} />
              ) : <Empty what={ua ? 'No activity trend for this window.' : 'User activity unavailable.'} />}
            </Panel>
            <Panel title="Districts by activity"
              right={districts && totalDistricts !== null ? `${districts.length} of ${totalDistricts}` : undefined}>
              {districts ? (
                <div className="pd-rows">
                  {districts.map((d, i) => (
                    <Bar key={d.name} name={d.name} value={num(d.calls)}
                      sub={`${d.units} ${d.units === 1 ? 'unit' : 'units'} · ${num(d.users)} users`}
                      width={districtMax ? (d.calls / districtMax) * 100 : 0} colour={wheel(i)} nameW={104} />
                  ))}
                  <p className="pd-note">Rolled up from the busiest units — it ranks the districts appearing there, not every district on record.</p>
                </div>
              ) : <Empty what={uu ? 'No district activity to roll up.' : 'Unit usage unavailable.'} />}
            </Panel>
          </div>

          <div className="pd-grid" style={{ borderTop: '1px solid var(--pd-line)' }}>
            <Panel title={`Busiest units · ${period}`}>
              {uu?.topUnits?.length ? (
                <>
                  <div className="pd-thead" style={{ gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) 64px 56px' }}>
                    <span>Unit</span><span>district</span><span>share of calls</span>
                    <span className="r">calls</span><span className="r">users</span>
                  </div>
                  {uu.topUnits.slice(0, 6).map((u) => (
                    <button key={u.unitId} type="button" className="pd-trow"
                      style={{ gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) 64px 56px' }}
                      onClick={() => navigate('/units?tab=units')}>
                      <span className="nm">{u.unitName}</span>
                      <span className="nm dim">{u.districtName ?? '—'}</span>
                      {/* A true share of the window's calls, because that is what
                          the column header claims. Scaling to the busiest unit
                          instead made the top row read as 100% of all calls. */}
                      <span className="track" style={{
                        height: 7, borderRadius: 2, overflow: 'hidden',
                        background: 'var(--pd-accent-track)', display: 'block',
                      }} title={unitCallTotal ? `${rate((u.totalCalls / unitCallTotal) * 100)} of calls in ${period}` : undefined}>
                        <i style={{
                          display: 'block', height: '100%', borderRadius: 2,
                          width: `${unitCallTotal ? Math.min((u.totalCalls / unitCallTotal) * 100, 100) : 0}%`,
                          background: V.accent,
                        }} />
                      </span>
                      <span className="n num">{num(u.totalCalls)}</span>
                      <span className="n num">{num(u.activeUsers)}</span>
                    </button>
                  ))}
                </>
              ) : <Empty what={uu ? 'No unit-level activity in this window.' : 'Unit usage unavailable.'} />}
            </Panel>
          </div>
        </section>
      )}

      {tip && (
        <div className="pd-tip" style={{
          left: Math.min(tip.x + 14, window.innerWidth - 250),
          top: Math.min(tip.y + 14, window.innerHeight - 140),
        }}>{tip.node}</div>
      )}
    </div>
  );
};

export default PlatformDashboard;
